import { FC, useMemo, useState } from 'react';

import {
  HalfDayCalendarProps,
  HalfDaySelection,
} from './HalfDayCalendar.props';

// Utilities
const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
const ymd = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number): Date => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const startOfMonth = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), 1);
const startOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfMonth = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfCalendar = (d: Date): Date => {
  const first = startOfMonth(d);
  const day = first.getDay(); // 0 Sun ... 6 Sat
  return addDays(first, -day); // start on Sunday
};
const endOfCalendar = (d: Date): Date => {
  const last = endOfMonth(d);
  const day = last.getDay();
  return addDays(last, 6 - day);
};

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const cn = (...xs: Array<string | undefined | false>): string =>
  xs.filter(Boolean).join(' ');
const fmtLong = (d: Date): string =>
  `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
const fmtDatePretty = (d: Date): string => {
  const wk = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  return `${wk}, ${months[d.getMonth()].slice(0, 3)} ${d.getDate()}`;
};

type HalfKey = 'am' | 'pm';
type CalendarBlock = { date: string; half: HalfKey };

const LegendSwatch: FC<{ className?: string; label?: string }> = ({
  className = '',
  label = '',
}) => (
  <div className="flex items-center gap-1.5">
    <div className={cn('h-3 w-3 rounded', className)} />
    <span>{label}</span>
  </div>
);

const HalfDayCalendarView: FC<HalfDayCalendarProps> = (props) => {
  const {
    availability = {},
    initialMonth = new Date(),
    onChange,
    className,
    minDate,
    onContinue,
  } = props;

  const [view, setView] = useState<Date>(new Date(initialMonth));
  const [selected, setSelected] = useState<HalfDaySelection>({});
  const [anchorIdx, setAnchorIdx] = useState<number | null>(null);
  const [endIdx, setEndIdx] = useState<number | null>(null);

  const days = useMemo(() => {
    const start = startOfCalendar(view);
    const end = endOfCalendar(view);
    const out: Date[] = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
      out.push(new Date(d));
    }
    return out;
  }, [view]);

  const nextMonth = (): void =>
    setView((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const prevMonth = (): void =>
    setView((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));

  const sortedCalendarBlocks = useMemo<CalendarBlock[]>(() => {
    return days.flatMap((d) => {
      const ds = ymd(d);
      return [
        { date: ds, half: 'am' as const },
        { date: ds, half: 'pm' as const },
      ];
    });
  }, [days]);

  const halfIndex = (dateStr: string, half: HalfKey): number => {
    return sortedCalendarBlocks.findIndex(
      (b) => b.date === dateStr && b.half === half,
    );
  };
  const indexToHalf = (idx: number): CalendarBlock => sortedCalendarBlocks[idx];

  const toggleHalf = (dateStr: string, which: HalfKey): void => {
    const idx = halfIndex(dateStr, which);
    if (idx === -1) return;

    const buildSelection = (start: number, end: number): HalfDaySelection => {
      const next: HalfDaySelection = {};
      for (let i = Math.min(start, end); i <= Math.max(start, end); i++) {
        const { date, half } = indexToHalf(i);
        const avail = availability[date] || { am: true, pm: true };
        if (!avail[half]) continue;
        const day = next[date] || { am: false, pm: false };
        day[half] = true;
        next[date] = day;
      }
      return next;
    };

    if (anchorIdx === null) {
      setAnchorIdx(idx);
      setEndIdx(null);
      const single = buildSelection(idx, idx);
      setSelected(single);
      onChange && onChange(single);
      return;
    }

    if (endIdx === null) {
      if (idx < anchorIdx) {
        setAnchorIdx(idx);
        setEndIdx(null);
        const single = buildSelection(idx, idx);
        setSelected(single);
        onChange && onChange(single);
      } else {
        setEndIdx(idx);
        const range = buildSelection(anchorIdx, idx);
        setSelected(range);
        onChange && onChange(range);
      }
      return;
    }

    if (idx >= anchorIdx && idx <= endIdx) {
      setEndIdx(idx);
      const range = buildSelection(anchorIdx, idx);
      setSelected(range);
      onChange && onChange(range);
      return;
    }

    if (idx > endIdx) {
      setEndIdx(idx);
      const range = buildSelection(anchorIdx, idx);
      setSelected(range);
      onChange && onChange(range);
      return;
    }

    setAnchorIdx(idx);
    setEndIdx(null);
    const single = buildSelection(idx, idx);
    setSelected(single);
    onChange && onChange(single);
  };

  const isSameMonth = (d: Date): boolean => d.getMonth() === view.getMonth();

  const renderDayCell = (d: Date) => {
    const ds = ymd(d);
    const isCurrentMonth = isSameMonth(d);
    const baseAvail = availability[ds] ?? { am: true, pm: true };
    const isBeforeMin = minDate ? startOfDay(d) < startOfDay(minDate) : false;
    const avail = isBeforeMin ? { am: false, pm: false } : baseAvail;
    const sel = selected[ds] || { am: false, pm: false };

    const baseBox =
      'relative rounded-xl border text-sm overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500' +
      (isCurrentMonth ? '' : ' opacity-40');

    const amClasses = cn(
      'block w-full text-left px-2 py-1 h-7 outline-none',
      avail.am
        ? sel.am
          ? 'bg-emerald-500/90 text-white'
          : 'bg-emerald-100 hover:bg-emerald-200'
        : 'bg-neutral-200 text-neutral-400 cursor-not-allowed',
    );
    const pmClasses = cn(
      'block w-full text-left px-2 py-1 h-7 outline-none',
      avail.pm
        ? sel.pm
          ? 'bg-emerald-500/90 text-white'
          : 'bg-emerald-100 hover:bg-emerald-200'
        : 'bg-neutral-200 text-neutral-400 cursor-not-allowed',
    );

    return (
      <div key={ds} className={baseBox} aria-label={ds}>
        <div className="absolute right-1 top-1 text-[11px] font-medium text-neutral-500 select-none">
          {d.getDate()}
        </div>

        <button
          type="button"
          title={`${fmtLong(d)} — AM ${avail.am ? 'available' : 'unavailable'}`}
          className={amClasses}
          disabled={!avail.am}
          onClick={() => toggleHalf(ds, 'am')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggleHalf(ds, 'am');
          }}>
          <span className="text-[11px]">AM</span>
        </button>

        <div className="h-px bg-white/60" />

        <button
          type="button"
          title={`${fmtLong(d)} — PM ${avail.pm ? 'available' : 'unavailable'}`}
          className={pmClasses}
          disabled={!avail.pm}
          onClick={() => toggleHalf(ds, 'pm')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggleHalf(ds, 'pm');
          }}>
          <span className="text-[11px]">PM</span>
        </button>
      </div>
    );
  };

  const summaryList = useMemo(() => {
    const keys = Object.keys(selected).sort();
    const items: Array<{ k: string; label: string; halves: string }> = [];
    keys.forEach((k) => {
      const s = selected[k];
      if (!s?.am && !s?.pm) return;
      const label = fmtDatePretty(new Date(k));
      const halves = [s.am ? 'AM' : null, s.pm ? 'PM' : null]
        .filter(Boolean)
        .join(', ');
      items.push({ k, label, halves });
    });
    return items;
  }, [selected]);

  const clearAll = (): void => {
    setSelected({});
    onChange && onChange({});
    setAnchorIdx(null);
    setEndIdx(null);
  };

  return (
    <div
      className={cn(
        'mx-auto max-w-6xl p-4 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6',
        className,
      )}>
      <div className="rounded-2xl border bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Previous month"
              className="px-3 py-2 rounded-lg border hover:bg-neutral-50"
              onClick={prevMonth}>
              ‹
            </button>
            <div className="text-lg font-semibold">
              {months[view.getMonth()]} {view.getFullYear()}
            </div>
            <button
              type="button"
              aria-label="Next month"
              className="px-3 py-2 rounded-lg border hover:bg-neutral-50"
              onClick={nextMonth}>
              ›
            </button>
          </div>
          <div className="hidden md:flex items-center gap-3 text-xs">
            <LegendSwatch className="bg-emerald-100" label="Available" />
            <LegendSwatch className="bg-neutral-200" label="Unavailable" />
            <LegendSwatch
              className="bg-emerald-500 text-white"
              label="Selected"
            />
          </div>
        </div>

        <div className="grid grid-cols-7 text-center text-xs text-neutral-500 mb-2">
          {'Sun Mon Tue Wed Thu Fri Sat'.split(' ').map((d) => (
            <div key={d} className="py-1">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {days.map((d) => renderDayCell(d))}
        </div>

        <div className="mt-4 text-xs text-neutral-600">
          Selection is linear: pick a start, then pick an end. Clicking outside
          the range resets the selection.
        </div>
      </div>

      <aside className="lg:sticky lg:top-4 h-fit rounded-2xl border bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold">Your Selection</h3>
          <button
            className="text-sm text-red-600 hover:underline"
            onClick={clearAll}>
            Clear all
          </button>
        </div>

        {summaryList.length === 0 ? (
          <p className="text-sm text-neutral-500">No time blocks selected.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {summaryList.map((it) => (
              <li
                key={it.k}
                className="flex items-center justify-between gap-2">
                <span>{it.label}</span>
                <span className="text-neutral-500">{it.halves}</span>
              </li>
            ))}
          </ul>
        )}

        <hr className="my-3" />
        {summaryList.length > 0 && (
          <button
            type="button"
            className="w-full py-2 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700"
            onClick={() => {
              onContinue && onContinue();
            }}>
            Continue
          </button>
        )}

        <div className="mt-4 flex md:hidden flex-wrap gap-2 text-xs">
          <LegendSwatch className="bg-emerald-100" label="Available" />
          <LegendSwatch className="bg-neutral-200" label="Unavailable" />
          <LegendSwatch
            className="bg-emerald-500 text-white"
            label="Selected"
          />
        </div>
      </aside>
    </div>
  );
};

export default HalfDayCalendarView;
